import { FC } from "react";
import * as _ from "lodash";
import { useLocation, useParams } from "react-router-dom";

import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { Charts, Analytics, Tag, Col } from "@ctra/components";
import { Enterprise } from "@ctra/api";

import { useFarm } from "@farms";
import { useDataDictionary } from "@base";
import { Chart, ChartAPIContext, ChartFilterContext } from "@chart";
import { CompiledRoutes } from "@routes";

import styles from "./MiniChart.module.less";

const {
  V3: { MiniChartCard }
} = Analytics;

interface MiniChartProps {
  metric: string;
  variant: string;
}

const {
  analytics: {
    config: {
      metric: { displayName },
      variant: variantCopy,
      source: sourceCopy,
      projections
    }
  },
  chart: { description }
} = Content;

/**
 * List of chart cards
 * @returns
 */
export const MiniChart: FC<MiniChartProps> = ({ metric, variant }) => {
  const { t } = useTranslation();
  const { farm } = useFarm();
  const { dashboard } = useParams<{ dashboard: string }>();
  const { search, state } = useLocation();
  const searchParams = new URLSearchParams(search);

  const {
    api: { getChartInfo },
    groups: { projectionMetrics },
    dataDescriptors
  } = useDataDictionary();

  /**
   * Default interval for the chart
   */
  const defaultInterval = _.get(dataDescriptors, [variant, "valueProperties", "defaultInterval"], "P3M");

  const { nameToDisplay, sourceNames, variantType } = getChartInfo(metric, variant);

  /**
   * Handle chart click event
   */
  const onChartClick = () => {
    const isCompareChartEntry = !dashboard;

    if (isCompareChartEntry) {
      Enterprise.history.push({
        pathname: CompiledRoutes.app.analytics.compareCharts.withMetric({
          source: "metric",
          objectID: metric
        })
      });
    } else {
      searchParams.set("variant", variant);

      Enterprise.history.push({
        pathname: CompiledRoutes.app.analytics.dashboard.addChart.metric({
          dashboard,
          metricID: metric
        }),
        search: searchParams.toString(),
        state
      });
    }
  };

  return (
    <ChartFilterContext.Provider isoDuration={defaultInterval}>
      <ChartAPIContext.Provider metricVariantID={variant} farmID={farm?.id}>
        <ChartAPIContext.Consumer>
          {({
            charts: [
              {
                entity: { title }
              }
            ]
          }) => (
            <MiniChartCard
              metric={t<string>(displayName(nameToDisplay), { makeDefaultValue: true })}
              source={
                sourceNames &&
                _.join(
                  _.map(sourceNames, (sourceName) => t<string>(sourceCopy(sourceName))),
                  ", "
                )
              }
              variant={t<string>(variantCopy(variantType))}
              tooltip={t<string>(description(title))}
              onClick={onChartClick}
            >
              <Chart
                variant={Charts.ChartVariant.Mini}
                config={{ legend: false, height: 195, tooltip: false }}
                viewOptions={{ showAnnotations: false, showAnomalies: false }}
              />
              <Col span={24} className={styles.Col}>
                {_.includes(projectionMetrics, metric) && (
                  <Tag className={styles.Tag}>{t<string>(projections)}</Tag>
                )}
              </Col>
            </MiniChartCard>
          )}
        </ChartAPIContext.Consumer>
      </ChartAPIContext.Provider>
    </ChartFilterContext.Provider>
  );
};
